import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiTrash2, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import Breadcrumbs from '../../components/Breadcrumbs';
import Input from '../../components/input';
import Select from '../../components/Select';
import Button from '../../components/button';
import LogsComments from '../../components/LogsComments';
import UserAvatar from '../../components/UserAvatar';
import { successNotify, errorNotify } from '../../utils/toast';
import SpinnerLoader from '../../utils/SpinnerLoader';
import { getTripDetails, fetchTripTimeline, setSelectedTripId } from '../../store/Slices/TripSlice';
import { fetchProductsDropdown, fetchCustomerDropdown } from '../../store/Slices/dropdownSlice';
import { apiService } from '../../services/apiService';
import { checkTripStagePermission, hasTripPermission, hasTripReachedStage, isSuperAdminRole } from '../../utils/helper';
import { TripPermission, TripsStageEnum } from '../../utils/app-constant';
import '../../styles/Trip.css';
import '../../styles/TripPlanningDetails.css';

const parseQuantityValue = (value) => {
    const numericValue = Number(String(value ?? '').trim());
    return Number.isNaN(numericValue) ? 0 : numericValue;
};

// A stop always has at least one activity on screen, even before the array is filled in.
const getStopActivityList = (stop = {}) =>
    Array.isArray(stop?.activities) && stop.activities.length > 0
        ? stop.activities
        : [{ type: stop?.stop_type, quantity: stop?.quantity }];

const getActivityType = (activity = {}, stop = {}) =>
    String(activity?.type || stop?.stop_type || '').trim().toLowerCase();

const sumQuantityByActivityType = (stops = [], activityType) =>
    (Array.isArray(stops) ? stops : []).reduce((total, stop) => (
        total + getStopActivityList(stop).reduce((stopTotal, activity) => (
            getActivityType(activity, stop) === activityType
                ? stopTotal + parseQuantityValue(activity?.quantity)
                : stopTotal
        ), 0)
    ), 0);

// Form Validation Schema
const tripPlanningSchema = Yup.object().shape({
    start_location: Yup.string().required("Start Location is required"),
    end_location: Yup.string().required("End Location is required"),
    stops: Yup.array()
        .of(
            Yup.object().shape({
                location: Yup.string().required("Stop Location is required"),
                activities: Yup.array().of(
                    Yup.object().shape({
                        type: Yup.string().required("Activity type is required"),
                        product: Yup.string().required("Product is required"),
                        quantity: Yup.string().required("Quantity is required")
                    })
                )
            })
        )
        // Whatever is loaded has to come off the truck, no matter how many stops it is
        // split across.
        .test('load-unload-balance', 'Unload quantity must match the loaded quantity', function (stops) {
            const loadedQuantity = sumQuantityByActivityType(stops, 'load');
            const unloadedQuantity = sumQuantityByActivityType(stops, 'unload');

            if (loadedQuantity === unloadedQuantity) return true;

            return this.createError({
                message: `Total unload quantity (${unloadedQuantity}) must match the total load quantity (${loadedQuantity})`
            });
        })
});

// Stop Type Options
const stopTypeOptions = [
    { label: 'Load', value: 'load' },
    { label: 'Unload', value: 'unload' },
    { label: 'Transfer', value: 'transfer' },
    { label: 'Return', value: 'return' },
    { label: 'Empty', value: 'empty' }
];

// Customer location records name their coordinates differently depending on the endpoint,
// so read the same candidate list the v1 Create Trip screen uses.
const getFirstDefinedValue = (candidates, fallback = '') => {
    for (const candidate of candidates) {
        if (candidate === '' || candidate === null || candidate === undefined) continue;
        return candidate;
    }
    return fallback;
};

const getLocationLatitude = (location = {}) => getFirstDefinedValue([
    location?.lat,
    location?.latitude,
    location?.location_latitude,
    location?.warehouse_latitude,
    location?.customer_warehouse_latitude,
], null);

const getLocationLongitude = (location = {}) => getFirstDefinedValue([
    location?.lng,
    location?.long,
    location?.longitude,
    location?.location_longitude,
    location?.warehouse_longitude,
    location?.customer_warehouse_longitude,
], null);

// updateTripStops rejects empty coordinates, and v1 sends 0 for a location that has none,
// so keep a real 0 instead of letting `Number(x) || null` turn it back into null.
const parseCoordinate = (value) => {
    if (value === '' || value === null || value === undefined) return 0;
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? 0 : numericValue;
};

const normalizeLocationText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

// The trip detail names its points ("Karachi Warehouse") differently from the dropdown
// labels ("Karachi Warehouse, Karachi"), so match on either side of the city suffix.
const findLocationOption = (options = [], locationLabel, locationId) => {
    const normalizedLabel = normalizeLocationText(locationLabel);

    return (Array.isArray(options) ? options : []).find((option) => {
        if (locationId !== '' && locationId !== null && locationId !== undefined) {
            if (String(option?.value) === String(locationId) || String(option?.id) === String(locationId)) {
                return true;
            }
        }

        if (!normalizedLabel) return false;

        const candidates = [option?.label, option?.name, option?.raw?.warehouse_name, option?.raw?.address]
            .map(normalizeLocationText)
            .filter(Boolean);

        return candidates.some((candidate) => (
            candidate === normalizedLabel ||
            candidate.startsWith(`${normalizedLabel},`) ||
            normalizedLabel.startsWith(`${candidate},`) ||
            candidate.startsWith(`${normalizedLabel} -`) ||
            normalizedLabel.startsWith(`${candidate} -`)
        ));
    }) || null;
};

// Unload has to equal what was loaded, so the single unload row tracks the load row while
// it is still the only one. Once the drop is split across several unload rows the user is
// distributing by hand, so leave those values alone.
const syncUnloadQuantityWithLoad = (values, changedStopIndex, changedActivityIndex, nextQuantity, setFieldValue) => {
    const stops = Array.isArray(values?.stops) ? values.stops : [];
    const changedStop = stops[changedStopIndex];
    if (!changedStop) return;

    const changedActivity = getStopActivityList(changedStop)[changedActivityIndex] || {};
    if (getActivityType(changedActivity, changedStop) !== 'load') return;

    let loadedQuantity = 0;
    const unloadTargets = [];

    stops.forEach((stop, stopIndex) => {
        getStopActivityList(stop).forEach((activity, activityIndex) => {
            const activityType = getActivityType(activity, stop);

            if (activityType === 'load') {
                loadedQuantity += (stopIndex === changedStopIndex && activityIndex === changedActivityIndex)
                    ? parseQuantityValue(nextQuantity)
                    : parseQuantityValue(activity?.quantity);
            } else if (activityType === 'unload') {
                unloadTargets.push({ stopIndex, activityIndex });
            }
        });
    });

    if (unloadTargets.length !== 1) return;

    const { stopIndex, activityIndex } = unloadTargets[0];
    const mirroredQuantity = String(nextQuantity ?? '').trim() === '' ? '' : String(loadedQuantity);
    setFieldValue(`stops[${stopIndex}].activities[${activityIndex}].quantity`, mirroredQuantity);
};

const syncUnloadProductWithLoad = (values, changedStopIndex, changedActivityIndex, selectedProductOption, setFieldValue) => {
    const stops = Array.isArray(values?.stops) ? values.stops : [];
    const prodLabel = selectedProductOption ? String(selectedProductOption.label || selectedProductOption.value || "") : "";
    const prodId = selectedProductOption ? String(selectedProductOption.id || selectedProductOption.value || "") : "";
    const prodRate = selectedProductOption ? String(selectedProductOption.productValue || selectedProductOption.product_value || selectedProductOption.rate || selectedProductOption.price || "") : "";

    stops.forEach((stop, stopIndex) => {
        if (stopIndex === changedStopIndex) return;

        getStopActivityList(stop).forEach((activity, activityIndex) => {
            const activityType = getActivityType(activity, stop);
            if (activityType === 'unload') {
                setFieldValue(`stops[${stopIndex}].activities[${activityIndex}].product`, prodLabel);
                setFieldValue(`stops[${stopIndex}].activities[${activityIndex}].product_id`, prodId);
                if (prodRate) {
                    setFieldValue(`stops[${stopIndex}].activities[${activityIndex}].product_value`, prodRate);
                }
                setFieldValue(`stops[${stopIndex}].product`, prodLabel);
                setFieldValue(`stops[${stopIndex}].product_id`, prodId);
                if (prodRate) {
                    setFieldValue(`stops[${stopIndex}].product_load_value`, prodRate);
                }
            }
        });
    });
};

const handleStartLocationChange = (opt, values, setFieldValue) => {
    const label = opt ? String(opt.label || opt.name || "") : "";
    const id = opt ? String(opt.id || opt.value || "") : "";
    const lat = opt ? getLocationLatitude(opt) : null;
    const lng = opt ? getLocationLongitude(opt) : null;
    const prov = opt?.province || "";

    setFieldValue("start_location", label);

    if (values.stops && values.stops.length > 0) {
        setFieldValue("stops[0].location", label);
        setFieldValue("stops[0].location_id", id);
        setFieldValue("stops[0].latitude", lat);
        setFieldValue("stops[0].longitude", lng);
        if (prov) setFieldValue("stops[0].province", prov);
    }
};

const handleStopLocationChange = (opt, sIdx, values, setFieldValue) => {
    const label = opt ? String(opt.label || opt.name || "") : "";
    const id = opt ? String(opt.id || opt.value || "") : "";
    const lat = opt ? getLocationLatitude(opt) : null;
    const lng = opt ? getLocationLongitude(opt) : null;
    const prov = opt?.province || "";

    setFieldValue(`stops[${sIdx}].location`, label);
    setFieldValue(`stops[${sIdx}].location_id`, id);
    setFieldValue(`stops[${sIdx}].latitude`, lat);
    setFieldValue(`stops[${sIdx}].longitude`, lng);
    if (prov) setFieldValue(`stops[${sIdx}].province`, prov);

    if (sIdx === 0) {
        setFieldValue("start_location", label);
    }

    if (values.stops && sIdx === values.stops.length - 1) {
        setFieldValue("end_location", label);
    }
};

const handleEndLocationChange = (opt, values, setFieldValue) => {
    const label = opt ? String(opt.label || opt.name || "") : "";
    const id = opt ? String(opt.id || opt.value || "") : "";
    const lat = opt ? getLocationLatitude(opt) : null;
    const lng = opt ? getLocationLongitude(opt) : null;
    const prov = opt?.province || "";

    setFieldValue("end_location", label);

    if (values.stops && values.stops.length > 0) {
        const lastIdx = values.stops.length - 1;
        setFieldValue(`stops[${lastIdx}].location`, label);
        setFieldValue(`stops[${lastIdx}].location_id`, id);
        setFieldValue(`stops[${lastIdx}].latitude`, lat);
        setFieldValue(`stops[${lastIdx}].longitude`, lng);
        if (prov) setFieldValue(`stops[${lastIdx}].province`, prov);
    }
};

const handleProductChange = (opt, sIdx, aIdx, act, stop, values, setFieldValue) => {
    if (opt) {
        const prodLabel = String(opt.label || opt.value || "");
        const prodRate = String(opt.productValue || opt.product_value || opt.rate || opt.price || act.product_value || "");
        const prodId = String(opt.id || opt.value || "");

        setFieldValue(`stops[${sIdx}].activities[${aIdx}].product`, prodLabel);
        setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_id`, prodId);
        if (prodRate) {
            setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_value`, prodRate);
        }
        setFieldValue(`stops[${sIdx}].product`, prodLabel);
        setFieldValue(`stops[${sIdx}].product_id`, prodId);
        if (prodRate) {
            setFieldValue(`stops[${sIdx}].product_load_value`, prodRate);
        }

        const currentType = getActivityType(act, stop);
        if (currentType === 'load' || sIdx === 0) {
            syncUnloadProductWithLoad(values, sIdx, aIdx, opt, setFieldValue);
        }
    } else {
        setFieldValue(`stops[${sIdx}].activities[${aIdx}].product`, "");
        setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_id`, "");
        setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_value`, "");
        setFieldValue(`stops[${sIdx}].product`, "");
        setFieldValue(`stops[${sIdx}].product_id`, "");
        setFieldValue(`stops[${sIdx}].product_load_value`, "");

        const currentType = getActivityType(act, stop);
        if (currentType === 'load' || sIdx === 0) {
            syncUnloadProductWithLoad(values, sIdx, aIdx, null, setFieldValue);
        }
    }
};

const getLoadStopProductInfo = (values) => {
    const stops = Array.isArray(values?.stops) ? values.stops : [];
    for (let sIdx = 0; sIdx < stops.length; sIdx++) {
        const stop = stops[sIdx];
        const acts = getStopActivityList(stop);
        for (const act of acts) {
            const actType = getActivityType(act, stop);
            if (actType === 'load' && act.product) {
                return {
                    product: typeof act.product === 'object' ? (act.product.label || act.product.name || act.product.value) : act.product,
                    product_id: act.product_id || stop.product_id || "",
                    product_value: act.product_value || stop.product_load_value || ""
                };
            }
        }
        if (stop.product) {
            return {
                product: typeof stop.product === 'object' ? (stop.product.label || stop.product.name || stop.product.value) : stop.product,
                product_id: stop.product_id || "",
                product_value: stop.product_load_value || ""
            };
        }
    }
    return { product: "", product_id: "", product_value: "" };
};

const getBlankStop = (defaultProduct = {}) => ({
    id: null,
    stop_number: 1,
    stop_type: "unload",
    location_id: "",
    location: "",
    latitude: null,
    longitude: null,
    province: "",
    tax_rule_id: null,
    product_id: defaultProduct.product_id || "",
    product: defaultProduct.product || "",
    quantity: "",
    product_load_value: defaultProduct.product_value || "",
    load_type: "full",
    activities: [
        { 
            id: `act_${Date.now()}`, 
            type: "unload", 
            product: defaultProduct.product || "", 
            product_id: defaultProduct.product_id || "", 
            quantity: "", 
            product_value: defaultProduct.product_value || "" 
        }
    ]
});

const TripPlanningDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const realTripId = useMemo(() => {
        if (!id) return null;
        return String(id).replace(/^TR-/i, '');
    }, [id]);

    const { tripDetailsData, tripDetailsLoading } = useSelector((state) => state.trips);
    const { productsDropdownData, customerDropdownData } = useSelector((state) => state.dropdowns || state.dropdown || {});

    const [customerLocations, setCustomerLocations] = React.useState([]);
    const [customerProducts, setCustomerProducts] = React.useState([]);
    const [fetchedCustomerName, setFetchedCustomerName] = React.useState('');
    const [isTripDetailsRequestPending, setIsTripDetailsRequestPending] = React.useState(() => Boolean(realTripId));
    const [loadedTripDetailsId, setLoadedTripDetailsId] = React.useState(null);



    useEffect(() => {
        let isActive = true;

        if (realTripId) {
            setIsTripDetailsRequestPending(true);
            dispatch(getTripDetails(realTripId)).finally(() => {
                if (isActive) {
                    setLoadedTripDetailsId(realTripId);
                    setIsTripDetailsRequestPending(false);
                }
            });
            dispatch(fetchTripTimeline(realTripId));
            dispatch(setSelectedTripId(realTripId));
        } else {
            setLoadedTripDetailsId(null);
            setIsTripDetailsRequestPending(false);
        }
        dispatch(fetchProductsDropdown());
        dispatch(fetchCustomerDropdown());

        return () => {
            isActive = false;
        };
    }, [realTripId, dispatch]);

    const detailData = useMemo(() => {
        return tripDetailsData?.data || tripDetailsData || {};
    }, [tripDetailsData]);

    useEffect(() => {
        const fetchCustomerDetailData = async () => {
            const custId = detailData?.customer_id || detailData?.customer?.id || detailData?.['3pl_customer_id'] || detailData?.customer_info?.id || (typeof detailData?.customer === 'number' ? detailData.customer : null);
            if (!custId) return;
            try {
                const response = await apiService.getCustomerTripDetail(custId);
                const data = response?.data || response || {};

                // Customer Name
                const cName = data?.name || data?.customer_name || data?.company_name || data?.title || (typeof detailData?.customer === 'string' ? detailData.customer : detailData?.customer?.name);
                if (cName) setFetchedCustomerName(cName);

                // Locations
                const locs = data?.locations || data?.warehouses || data?.customer_locations || data?.customer_warehouses || [];
                const formattedLocs = (Array.isArray(locs) ? locs : []).map((l) => {
                    const locName = l.warehouse_name || l.name || l.label || l.address || `Location #${l.id}`;
                    const cityStr = l.city ? `, ${l.city}` : '';
                    return {
                        value: String(l.id || l.value),
                        label: `${locName}${cityStr}`,
                        id: l.id,
                        name: locName,
                        latitude: getLocationLatitude(l),
                        longitude: getLocationLongitude(l),
                        province: l.province || l.state || "",
                        raw: l
                    };
                });
                setCustomerLocations(formattedLocs);

                // Fetch Products Rates By Date (matching TripDetails.jsx)
                const startDateStr = detailData?.start_at || detailData?.start_date || detailData?.created_at;
                const dateFormatted = startDateStr ? String(startDateStr).split('T')[0] : new Date().toISOString().split('T')[0];

                try {
                    const rateResp = await apiService.getProductRatesByDate({
                        customer_id: Number(custId),
                        date: dateFormatted
                    });
                    const prods = rateResp?.data || rateResp || [];
                    if (Array.isArray(prods) && prods.length > 0) {
                        const formattedProds = prods.map((p) => {
                            const rawName = String(p?.name || p?.title || p?.label || p?.product_name || p?.product || '').trim();
                            const rawUnit = String(p?.unit || p?.product_unit || p?.unit_name || '').trim();
                            let lbl = rawName;
                            if (rawUnit && !rawName.includes(`(${rawUnit})`)) {
                                lbl = `${rawName} (${rawUnit})`.trim();
                            }
                            const val = String(p?.id ?? p?.product_id ?? p?.value ?? lbl).trim();
                            const rate = String(p?.product_rate ?? p?.product_value ?? p?.product_load_value ?? p?.rate ?? p?.price ?? '');
                            return {
                                ...p,
                                value: val || lbl,
                                label: lbl || "Product",
                                id: p?.id || p?.product_id,
                                productValue: rate,
                                raw: p
                            };
                        });
                        setCustomerProducts(formattedProds);
                        return;
                    }
                } catch (rateErr) {
                    console.error("Error fetching product rates by date:", rateErr);
                }

                // Products fallback from customer trip detail
                const prods = data?.products || data?.customer_products || data?.product_rates || data?.items || [];
                const formattedProds = (Array.isArray(prods) ? prods : []).map((p) => {
                    const rawName = String(p?.name || p?.title || p?.label || p?.product_name || p?.product || '').trim();
                    const rawUnit = String(p?.unit || p?.product_unit || p?.unit_name || '').trim();
                    let lbl = rawName;
                    if (rawUnit && !rawName.includes(`(${rawUnit})`)) {
                        lbl = `${rawName} (${rawUnit})`.trim();
                    }
                    const val = String(p?.id ?? p?.product_id ?? p?.value ?? lbl).trim();
                    const rate = String(p?.product_load_value ?? p?.product_value ?? p?.product_rate ?? p?.rate ?? p?.price ?? '');
                    return {
                        value: val || lbl,
                        label: lbl || "Product",
                        id: p?.id || p?.product_id,
                        productValue: rate,
                        raw: p
                    };
                });
                setCustomerProducts(formattedProds);
            } catch (err) {
                console.error("Error fetching customer trip detail:", err);
            }
        };
        fetchCustomerDetailData();
    }, [detailData]);

    const customerName = useMemo(() => {
        const custObj = detailData?.customer;
        if (typeof custObj === 'object' && custObj !== null) {
            const nameStr = custObj.name || custObj.label || custObj.title || custObj.customer_name || custObj.company_name;
            if (nameStr) return nameStr;
        }

        const custId = detailData?.customer_id || (typeof custObj === 'number' || (typeof custObj === 'string' && !isNaN(custObj)) ? custObj : null);
        if (Array.isArray(customerDropdownData) && customerDropdownData.length > 0 && custId) {
            const foundCust = customerDropdownData.find(c => String(c.value || c.id) === String(custId));
            if (foundCust) return foundCust.label || foundCust.name || foundCust.title;
        }

        return fetchedCustomerName ||
               (typeof detailData?.customer_name === 'string' ? detailData.customer_name : '') || 
               (typeof detailData?.customer === 'string' && isNaN(detailData.customer) ? detailData.customer : '') || 
               '—';
    }, [detailData, customerDropdownData, fetchedCustomerName]);

    const vehicleReg = detailData?.vehicle?.registration_number || detailData?.vehicle_id || '—';
    const driverName = detailData?.primary_driver_name || detailData?.primary_driver?.name || detailData?.primary_driver?.username || '—';
    const driverPhone = detailData?.primary_driver_contact ? `+${detailData.primary_driver_contact}` : '—';
    const driverImage = detailData?.primary_driver?.image || detailData?.primary_driver?.avatar || null;
    const tripIdDisplay = detailData?.trip_id || id || "—";

    const productOptions = useMemo(() => {
        if (Array.isArray(customerProducts) && customerProducts.length > 0) {
            return customerProducts;
        }

        const stopProducts = [];
        const rawStops = detailData?.trip_stops || detailData?.stops || [];
        if (Array.isArray(rawStops)) {
            rawStops.forEach(stop => {
                const pObj = stop?.product;
                const pId = String(stop?.product_id || pObj?.id || pObj?.product_id || pObj?.value || '');
                const rawPName = String(pObj?.name || pObj?.title || pObj?.label || stop?.product_name || stop?.product_title || (typeof pObj === 'string' ? pObj : '')).trim();
                const rawPUnit = String(pObj?.unit || pObj?.product_unit || stop?.product_unit || '').trim();
                let pTitle = rawPName;
                if (rawPUnit && !rawPName.includes(`(${rawPUnit})`)) {
                    pTitle = `${rawPName} (${rawPUnit})`.trim();
                }

                if (pId || pTitle) {
                    const existing = stopProducts.find(o => String(o.value) === pId || o.label.toLowerCase() === pTitle.toLowerCase());
                    if (!existing) {
                        stopProducts.push({
                            value: pTitle || pId,
                            label: pTitle || `Product #${pId}`,
                            id: pId,
                            productValue: String(stop?.product_load_value ?? stop?.product_value ?? stop?.product_rate ?? '')
                        });
                    }
                }
            });
        }

        if (stopProducts.length > 0) return stopProducts;

        const apiOptions = (Array.isArray(productsDropdownData) ? productsDropdownData : []).map(item => {
            const rawName = String(item?.title ?? item?.name ?? item?.label ?? item?.product ?? '').trim();
            const rawUnit = String(item?.unit ?? item?.product_unit ?? item?.unit_name ?? '').trim();
            let label = rawName;
            if (rawUnit && !rawName.includes(`(${rawUnit})`)) {
                label = `${rawName} (${rawUnit})`.trim();
            }
            return {
                ...item,
                value: String(item?.value ?? item?.id ?? item?.product_id ?? label),
                label: label || `Product #${item?.id}`
            };
        });

        return apiOptions;
    }, [customerProducts, detailData, productsDropdownData]);

    const locationSelectOptions = useMemo(() => {
        if (!Array.isArray(customerLocations)) return [];
        const unique = [];
        const seen = new Set();
        for (const loc of customerLocations) {
            const key = String(loc.id ?? loc.value ?? loc.label ?? '').trim().toLowerCase();
            if (key && !seen.has(key)) {
                seen.add(key);
                unique.push(loc);
            }
        }
        return unique;
    }, [customerLocations]);

    const initialValues = useMemo(() => {
        const rawStops = detailData?.trip_stops || detailData?.stops || [];
        const sortedStops = Array.isArray(rawStops) && rawStops.length > 0
            ? [...rawStops].sort((a, b) => (Number(a?.stop_number) || 0) - (Number(b?.stop_number) || 0))
            : [];

        const getLocString = (loc) => {
            if (!loc) return '';
            if (typeof loc === 'object') {
                return loc.warehouse_name || loc.name || loc.location_point || loc.address || '';
            }
            return String(loc);
        };

        const startLoc = getFirstDefinedValue([
            detailData?.dispatch_point,
            detailData?.start_location,
            detailData?.origin,
            getLocString(sortedStops[0]?.location)
        ], '');
        const endLoc = getFirstDefinedValue([
            detailData?.decanting_point,
            detailData?.end_location,
            detailData?.destination,
            getLocString(sortedStops[sortedStops.length - 1]?.location)
        ], '');

        // A trip created from the v2 drawer carries no dispatch or decanting point yet, so the
        // first two stops have nothing to resolve. Seed them from the customer's own locations
        // and let the user change them; stops added by hand are left blank.
        const getDefaultStopOption = (stopIndex) => {
            if (stopIndex === 0) return customerLocations[0] || null;
            if (stopIndex === 1) return customerLocations[1] || customerLocations[0] || null;
            return null;
        };

        // The trip already carries its dispatch and decanting points, so pre-select them on the
        // first two stops instead of leaving a bare label the Select cannot resolve.
        const buildLocationFields = (locationLabel, locationId, fallback = {}, stopIndex = null) => {
            let matchedOption = findLocationOption(customerLocations, locationLabel, locationId);

            if (!matchedOption && !locationLabel && !locationId && stopIndex !== null) {
                matchedOption = getDefaultStopOption(stopIndex);
            }

            if (!matchedOption) {
                return {
                    location: locationLabel || "",
                    location_id: locationId || "",
                    latitude: fallback.latitude ?? null,
                    longitude: fallback.longitude ?? null,
                    province: fallback.province || ""
                };
            }

            return {
                location: matchedOption.label || locationLabel || "",
                location_id: matchedOption.id || matchedOption.value || locationId || "",
                latitude: getFirstDefinedValue([getLocationLatitude(matchedOption), fallback.latitude], null),
                longitude: getFirstDefinedValue([getLocationLongitude(matchedOption), fallback.longitude], null),
                province: matchedOption.province || fallback.province || ""
            };
        };

        const formattedStops = sortedStops.length >= 2 ? sortedStops.map((stop, idx) => {
            const pObj = stop?.product;
            const pId = String(stop?.product_id || pObj?.id || pObj?.product_id || pObj?.value || '');
            const rawPName = String(pObj?.name || pObj?.title || pObj?.label || stop?.product_name || stop?.product_title || (typeof pObj === 'string' ? pObj : '')).trim();
            const rawPUnit = String(pObj?.unit || pObj?.product_unit || stop?.product_unit || '').trim();
            let pTitle = rawPName;
            if (rawPUnit && !rawPName.includes(`(${rawPUnit})`)) {
                pTitle = `${rawPName} (${rawPUnit})`.trim();
            }

            const matchedOption = productOptions.find(o => 
                String(o.value) === String(pId) || 
                String(o.id) === String(pId) || 
                (rawPName && String(o.label).toLowerCase().includes(rawPName.toLowerCase()))
            );

            const resolvedProductVal = matchedOption ? matchedOption.label : (pTitle || pId || "");
            const resolvedProductRate = String(stop?.product_load_value ?? stop?.product_value ?? stop?.product_rate ?? pObj?.product_value ?? pObj?.product_rate ?? pObj?.rate ?? "");

            const rawLocation = stop?.location;
            const locName = typeof rawLocation === 'object' 
                ? (rawLocation.warehouse_name || rawLocation.name || rawLocation.location_point || rawLocation.address || '') 
                : String(rawLocation || '');
            const locCity = typeof rawLocation === 'object' ? (rawLocation.city || '') : (stop?.city || '');
            const fallbackLoc = idx === 0 ? startLoc : (idx === sortedStops.length - 1 ? endLoc : "");
            const stopLoc = idx === 0
                ? (startLoc || (locName ? (locCity ? `${locName} - ${locCity}` : locName) : ""))
                : (idx === sortedStops.length - 1
                    ? (endLoc || (locName ? (locCity ? `${locName} - ${locCity}` : locName) : ""))
                    : (locName ? (locCity ? `${locName} - ${locCity}` : locName) : fallbackLoc));

            const locationFields = buildLocationFields(
                stopLoc,
                stop?.location_id || stop?.warehouse_id || "",
                {
                    latitude: getFirstDefinedValue([
                        getLocationLatitude(stop),
                        typeof rawLocation === 'object' ? getLocationLatitude(rawLocation) : null,
                    ], null),
                    longitude: getFirstDefinedValue([
                        getLocationLongitude(stop),
                        typeof rawLocation === 'object' ? getLocationLongitude(rawLocation) : null,
                    ], null),
                    province: stop?.province || stop?.state || (typeof rawLocation === 'object' ? (rawLocation.province || rawLocation.state || "") : "")
                },
                idx
            );

            return {
                id: stop?.id || `stop_${idx + 1}`,
                stop_number: stop?.stop_number || (idx + 1),
                stop_type: stop?.stop_type || (idx === 0 ? "load" : "unload"),
                ...locationFields,
                tax_rule_id: stop?.tax_rule_id || null,
                product_id: pId || "",
                product: resolvedProductVal,
                quantity: stop?.quantity ? String(stop.quantity) : "",
                product_load_value: resolvedProductRate,
                load_type: stop?.load_type || "full",
                activities: [
                    {
                        id: `act_${idx}_1`,
                        type: stop?.stop_type || (idx === 0 ? "load" : "unload"),
                        product: resolvedProductVal,
                        product_id: pId || "",
                        quantity: stop?.quantity ? String(stop.quantity) : "",
                        product_value: resolvedProductRate
                    }
                ]
            };
        }) : [
            {
                id: "stop_1",
                stop_number: 1,
                stop_type: "load",
                ...buildLocationFields(startLoc, "", {}, 0),
                tax_rule_id: null,
                product_id: "",
                product: "",
                quantity: "",
                product_load_value: "",
                load_type: "full",
                activities: [
                    { id: "act_1", type: "load", product: "", product_id: "", quantity: "", product_value: "" }
                ]
            },
            {
                id: "stop_2",
                stop_number: 2,
                stop_type: "unload",
                ...buildLocationFields(endLoc, "", {}, 1),
                tax_rule_id: null,
                product_id: "",
                product: "",
                quantity: "",
                product_load_value: "",
                load_type: "full",
                activities: [
                    { id: "act_2", type: "unload", product: "", product_id: "", quantity: "", product_value: "" }
                ]
            }
        ];

        // Pre-fill unload product from load product if load has one and unload doesn't
        if (formattedStops.length >= 2 && formattedStops[0]?.product) {
            for (let i = 1; i < formattedStops.length; i++) {
                if (!formattedStops[i].product) {
                    formattedStops[i].product = formattedStops[0].product;
                    formattedStops[i].product_id = formattedStops[0].product_id;
                    formattedStops[i].product_load_value = formattedStops[0].product_load_value;
                    if (formattedStops[i].activities && formattedStops[i].activities[0]) {
                        formattedStops[i].activities[0].product = formattedStops[0].product;
                        formattedStops[i].activities[0].product_id = formattedStops[0].product_id;
                        formattedStops[i].activities[0].product_value = formattedStops[0].product_load_value;
                    }
                }
            }
        }

        // Keep the Start/End Location selects on the same resolved labels as the first and last
        // stop, otherwise they fall back to a raw dispatch point string that matches no option.
        return {
            start_location: formattedStops[0]?.location || startLoc,
            end_location: formattedStops[formattedStops.length - 1]?.location || endLoc,
            stops: formattedStops,
            general_notes: detailData?.general_notes || detailData?.notes || ""
        };
    }, [detailData, customerLocations]);

    const handleFormSubmit = async (values, { setSubmitting }) => {
        if (isTripPastInTransit) {
            errorNotify("This trip has already moved past Trip Stops and can no longer be edited");
            setSubmitting(false);
            return;
        }

        try {
            const payload = {
                trip_id: Number(realTripId || detailData?.id),
                note: values.general_notes || "",
                trip_stops: values.stops.flatMap((stop, idx) => {
                    const acts = Array.isArray(stop.activities) && stop.activities.length > 0
                        ? stop.activities
                        : [{ type: stop.stop_type, product: stop.product, product_id: stop.product_id, quantity: stop.quantity, product_value: stop.product_load_value }];

                    // Stops loaded from an older trip can come back without coordinates, so fall
                    // back to the selected customer location before the API rejects the save.
                    const matchedLocation = locationSelectOptions.find((opt) =>
                        String(opt.value) === String(stop.location_id) ||
                        String(opt.id) === String(stop.location_id) ||
                        opt.label === stop.location
                    );
                    const latitude = parseCoordinate(getFirstDefinedValue([
                        stop.latitude,
                        getLocationLatitude(matchedLocation || {}),
                    ], null));
                    const longitude = parseCoordinate(getFirstDefinedValue([
                        stop.longitude,
                        getLocationLongitude(matchedLocation || {}),
                    ], null));

                    return acts.map((act) => ({
                        stop_number: idx + 1,
                        stop_type: act.type || stop.stop_type || "unload",
                        product_id: Number(act.product_id || act.product || stop.product_id || stop.product) || null,
                        quantity: String(act.quantity || stop.quantity || ""),
                        product_load_value: String(act.product_value || stop.product_load_value || ""),
                        load_type: stop.load_type || "full",
                        location_id: Number(stop.location_id) || null,
                        location: typeof stop.location === 'string' ? stop.location : (stop.location?.label || ""),
                        latitude,
                        longitude,
                        province: stop.province || matchedLocation?.province || "",
                        tax_rule_id: Number(stop.tax_rule_id) || null
                    }));
                })
            };

            await apiService.updateTripStops(payload);
            successNotify("Trip Planning Created Successfully!");
            const dispatchTripId = realTripId || detailData?.id || id;
            if (dispatchTripId) {
                try {
                    await dispatch(getTripDetails(dispatchTripId)).unwrap();
                } catch {
                    // ignore and proceed
                }
            }
            const nextStageCheck = checkTripStagePermission("checklists-and-allocations");
            if (nextStageCheck.allowed) {
                navigate(`/trips-list/loading-dispatch/${dispatchTripId}`, { replace: true });
            } else {
                navigate("/trips-list", { replace: true });
            }
        } catch (err) {
            console.error("Error updating trip stops:", err);
            errorNotify(err || "Failed to update trip stops");
        } finally {
            setSubmitting(false);
        }
    };

    const canSaveTripPlanning = useMemo(() => {
        return isSuperAdminRole() || hasTripPermission([
            TripPermission.Created,
            TripPermission.TripStops,
            TripPermission.Update,
            "trip-planning",
            "planning",
            "trip-created",
            "create",
        ]);
    }, []);

    // Once the trip has moved on to In Transit (or beyond), this stage's data is
    // locked for editing - still viewable via Back, but Save must stay disabled.
    const isTripPastInTransit = useMemo(
        () => hasTripReachedStage(detailData?.stage, TripsStageEnum.InTransit),
        [detailData?.stage]
    );

    const isTripDetailsLoading = Boolean(
        realTripId &&
        (tripDetailsLoading || isTripDetailsRequestPending || loadedTripDetailsId !== realTripId)
    );

    if (isTripDetailsLoading) {
        return <SpinnerLoader />;
    }

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            validationSchema={tripPlanningSchema}
            onSubmit={handleFormSubmit}
        >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, handleSubmit }) => (
                <Form className="trip_planning_details_page">
                    {/* Desktop Breadcrumbs & Top Header Bar with Project Button Component */}
                    <div className="desktop_header_only">
                        <Breadcrumbs values={["Trip Management", "Trip Detail", "Planning"]} />
                        <div className="access_view planning_top_bar">
                            <h2>Trip Planning</h2>
                            <div className="planning_top_actions">
                                <Button
                                    cancelBtn={() => navigate("/trips-list")}
                                    loading={isSubmitting}
                                    disabled={isSubmitting || isTripPastInTransit}
                                    submitText={isSubmitting ? "Saving..." : "Save"}
                                    type="button"
                                    shouldRow={false}
                                    isShowSubmitBtn={canSaveTripPlanning}
                                    submitBtn={handleSubmit}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Mobile Header Bar with Exact Create Trip Style Button */}
                    <div className="mobile_header_only mobile_planning_header">
                        <div className="mobile_header_left_controls">
                            <button className="mobile_menu_icon_btn" onClick={() => navigate("/trips-list")} type="button" title="Back">
                                <FiArrowLeft style={{ fontSize: '20px' }} />
                            </button>
                            <h1 className="mobile_header_title">Trip Planning</h1>
                        </div>
                        <div className="mobile_header_right_controls">
                            {canSaveTripPlanning && (
                                <button className="btn_figma_primary" type="submit" disabled={isSubmitting || isTripPastInTransit}>
                                    {isSubmitting ? "Saving..." : "Save"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Main Content Layout (2-Column Desktop, 1-Column Mobile) */}
                    <Row className="planning_main_row g-3">
                        {/* LEFT COLUMN: Route & Stops */}
                        <Col lg={8} md={12} className="planning_left_col">
                            <div className="planning_card route_stops_card">
                                {/* Card Header & Purple Banner */}
                                <div className="route_card_header">
                                    <div>
                                        <h3 className="route_card_title">Route & Stops</h3>
                                        <p className="route_card_subtext">Reorder stops with the arrows — the rail shows the drive order.</p>
                                    </div>
                                    <div className="trip_id_banner_pill">
                                        Trip ID: {tripIdDisplay}
                                    </div>
                                </div>

                                {/* Metadata Box Row */}
                                <div className="trip_meta_info_grid">
                                    <div className="meta_item">
                                        <span className="meta_label">Customer</span>
                                        <span className="meta_value_bold">{customerName}</span>
                                    </div>
                                    <div className="meta_item">
                                        <span className="meta_label">Vehicle</span>
                                        <a href="#vehicle" className="meta_link_blue">{vehicleReg}</a>
                                    </div>
                                    <div className="meta_item">
                                        <span className="meta_label">Driver</span>
                                        <div className="driver_avatar_text_row">
                                            <UserAvatar username={driverName} image={driverImage} size={24} />
                                            <span className="driver_name_purple">{driverName}</span>
                                        </div>
                                    </div>
                                    <div className="meta_item">
                                        <span className="meta_label">Driver Contact</span>
                                        <span className="meta_value_dark">{driverPhone}</span>
                                    </div>
                                </div>

                                {/* Rail & Drive Order Timeline */}
                                <div className="timeline_rail_container">
                                    {/* Start Location Node */}
                                    <div className="timeline_step_item">
                                        <div className="timeline_node node_start">
                                            <span>✓</span>
                                        </div>
                                        <div className="timeline_content_box">
                                            {/* <div className="location_input_header_row">
                                                <button className="set_on_map_btn" type="button">Set on Map ▼</button>
                                            </div> */}
                                            <Select 
                                                label="Start Location"
                                                name="start_location"
                                                placeholder="Select Start Location"
                                                options={locationSelectOptions}
                                                value={locationSelectOptions.find(o => o.label === values.start_location || String(o.value) === String(values.start_location)) || (values.start_location ? { label: values.start_location, value: values.start_location } : null)}
                                                onChange={(opt) => handleStartLocationChange(opt, values, setFieldValue)}
                                                onBlur={handleBlur}
                                                error={errors.start_location}
                                                touched={touched.start_location}
                                                required={true}
                                            />
                                        </div>
                                    </div>

                                    {/* Dynamic Stops FieldArray */}
                                    <FieldArray name="stops">
                                        {({ push, remove }) => (
                                            <>
                                                {values.stops.map((stop, sIdx) => {
                                                    const isMandatoryStop = values.stops.length <= 2;
                                                    const matchedLoc = locationSelectOptions.find(o => o.label === stop.location || String(o.value) === String(stop.location_id) || String(o.value) === String(stop.location)) || (stop.location ? { label: stop.location, value: String(stop.location_id || stop.location) } : null);
                                                    return (
                                                        <div className="timeline_step_item" key={stop.id || sIdx}>
                                                            <div className="timeline_node node_stop">
                                                                <span>{sIdx + 1}</span>
                                                            </div>
                                                            <div className="timeline_content_box stop_content_card">
                                                                {/* Stop Top Bar */}
                                                                <div className="stop_top_bar">
                                                                    <h4 className="stop_title">Stop-{sIdx + 1} ({sIdx === 0 ? 'Load' : sIdx === values.stops.length - 1 ? 'Unload' : 'Stop'})</h4>
                                                                    <div className="stop_controls">
                                                                        {/* <button className="stop_arrow_btn" type="button"><FiChevronUp /></button>
                                                                        <button className="stop_arrow_btn" type="button"><FiChevronDown /></button> */}
                                                                        {!isMandatoryStop && !isTripPastInTransit && (
                                                                            <button 
                                                                                className="stop_remove_btn" 
                                                                                type="button" 
                                                                                onClick={() => {
                                                                                    const remainingStops = values.stops.filter((_, idx) => idx !== sIdx);
                                                                                    remove(sIdx);
                                                                                    if (remainingStops.length > 0) {
                                                                                        if (sIdx === values.stops.length - 1) {
                                                                                            const newLast = remainingStops[remainingStops.length - 1];
                                                                                            setFieldValue("end_location", newLast.location || "");
                                                                                        }
                                                                                        if (sIdx === 0) {
                                                                                            const newFirst = remainingStops[0];
                                                                                            setFieldValue("start_location", newFirst.location || "");
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                title="Remove Stop"
                                                                            >
                                                                                <FiTrash2 /> <span className="stop_remove_text">Remove Stop</span>
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Location Select Input */}
                                                                {/* <div className="location_input_header_row">
                                                                    <button className="set_on_map_btn" type="button">Set on Map ▼</button>
                                                                </div> */}
                                                                <Select 
                                                                    label="Location"
                                                                    name={`stops[${sIdx}].location`}
                                                                    placeholder="Select Location"
                                                                    options={locationSelectOptions}
                                                                    value={matchedLoc}
                                                                    onChange={(opt) => handleStopLocationChange(opt, sIdx, values, setFieldValue)}
                                                                    onBlur={handleBlur}
                                                                    error={errors.stops?.[sIdx]?.location}
                                                                    touched={touched.stops?.[sIdx]?.location}
                                                                    required={true}
                                                                />
                                                                {/* Activities FieldArray */}
                                                                <FieldArray name={`stops[${sIdx}].activities`}>
                                                                    {({ push: pushAct, remove: removeAct }) => {
                                                                        const actsList = Array.isArray(stop.activities) && stop.activities.length > 0
                                                                            ? stop.activities
                                                                            : [{ id: `act_${sIdx}_0`, type: stop.stop_type || "unload", product: stop.product || "", quantity: stop.quantity || "", product_value: stop.product_load_value || "" }];

                                                                        return (
                                                                            <div className="stop_activities_container">
                                                                                {actsList.map((act, aIdx) => (
                                                                                    <div className="activity_item_card" key={act.id || aIdx}>
                                                                                        {/* Activity Type Dropdown Component */}
                                                                                        <div className="activity_type_select_wrapper">
                                                                                            <Select 
                                                                                                label="Activity"
                                                                                                name={`stops[${sIdx}].activities[${aIdx}].type`}
                                                                                                placeholder="Select Activity"
                                                                                                options={stopTypeOptions}
                                                                                                value={stopTypeOptions.find(o => o.value === act.type) || (act.type ? { label: act.type, value: act.type } : null)}
                                                                                                onChange={(opt) => {
                                                                                                    const newType = opt ? opt.value : "unload";
                                                                                                    setFieldValue(`stops[${sIdx}].activities[${aIdx}].type`, newType);
                                                                                                    setFieldValue(`stops[${sIdx}].stop_type`, newType);
                                                                                                    if (newType === 'unload' && !act.product) {
                                                                                                        const loadProd = getLoadStopProductInfo(values);
                                                                                                        if (loadProd.product) {
                                                                                                            setFieldValue(`stops[${sIdx}].activities[${aIdx}].product`, loadProd.product);
                                                                                                            setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_id`, loadProd.product_id);
                                                                                                            if (loadProd.product_value) {
                                                                                                                setFieldValue(`stops[${sIdx}].activities[${aIdx}].product_value`, loadProd.product_value);
                                                                                                            }
                                                                                                            setFieldValue(`stops[${sIdx}].product`, loadProd.product);
                                                                                                            setFieldValue(`stops[${sIdx}].product_id`, loadProd.product_id);
                                                                                                            if (loadProd.product_value) {
                                                                                                                setFieldValue(`stops[${sIdx}].product_load_value`, loadProd.product_value);
                                                                                                            }
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                                required={true}
                                                                                            />
                                                                                        </div>

                                                                                        {/* Product, Quantity, Value Inputs */}
                                                                                        <div className="activity_inputs_row">
                                                                                            <div className="activity_field_col flex_2">
                                                                                                <Select 
                                                                                                    label="Product"
                                                                                                    name={`stops[${sIdx}].activities[${aIdx}].product`}
                                                                                                    placeholder="Select Product"
                                                                                                    options={productOptions}
                                                                                                    value={(() => {
                                                                                                        if (!act.product) return null;
                                                                                                        const pStr = String(typeof act.product === 'object' ? (act.product.label || act.product.name || act.product.value) : act.product).trim().toLowerCase();
                                                                                                        if (!pStr) return null;
                                                                                                        const found = productOptions.find(o => 
                                                                                                            String(o.value).trim().toLowerCase() === pStr || 
                                                                                                            String(o.id || '').trim().toLowerCase() === pStr || 
                                                                                                            String(o.label).trim().toLowerCase() === pStr ||
                                                                                                            String(o.label).trim().toLowerCase().startsWith(pStr)
                                                                                                        );
                                                                                                        if (found) return found;
                                                                                                        return { label: typeof act.product === 'object' ? (act.product.label || act.product.name) : String(act.product), value: String(act.product) };
                                                                                                    })()}
                                                                                                    onChange={(opt) => handleProductChange(opt, sIdx, aIdx, act, stop, values, setFieldValue)}
                                                                                                    onBlur={handleBlur}
                                                                                                    error={errors.stops?.[sIdx]?.activities?.[aIdx]?.product}
                                                                                                    touched={touched.stops?.[sIdx]?.activities?.[aIdx]?.product}
                                                                                                    required={true}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="activity_field_col flex_1">
                                                                                                <Input
                                                                                                    label="Quantity"
                                                                                                    name={`stops[${sIdx}].activities[${aIdx}].quantity`}
                                                                                                    type="text"
                                                                                                    placeholder="00"
                                                                                                    value={act.quantity}
                                                                                                    onChange={(e) => {
                                                                                                        handleChange(e);
                                                                                                        syncUnloadQuantityWithLoad(values, sIdx, aIdx, e.target.value, setFieldValue);
                                                                                                    }}
                                                                                                    onBlur={handleBlur}
                                                                                                    error={errors.stops?.[sIdx]?.activities?.[aIdx]?.quantity}
                                                                                                    touched={touched.stops?.[sIdx]?.activities?.[aIdx]?.quantity}
                                                                                                    required={true}
                                                                                                />
                                                                                            </div>
                                                                                            <div className="activity_field_col flex_1">
                                                                                                <Input 
                                                                                                    label="Product Value"
                                                                                                    name={`stops[${sIdx}].activities[${aIdx}].product_value`}
                                                                                                    type="text"
                                                                                                    placeholder="00"
                                                                                                    value={act.product_value}
                                                                                                    onChange={handleChange}
                                                                                                    onBlur={handleBlur}
                                                                                                    disabled={true}
                                                                                                    required={false}
                                                                                                />
                                                                                            </div>
                                                                                            {actsList.length > 1 && (
                                                                                                <button 
                                                                                                    className="activity_delete_btn" 
                                                                                                    type="button" 
                                                                                                    onClick={() => removeAct(aIdx)}
                                                                                                >
                                                                                                    ✕
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                ))}

                                                                                {/* Add Activity Button Link */}
                                                                                {/* <button 
                                                                                    className="add_activity_link_btn" 
                                                                                    type="button" 
                                                                                    disabled={true}
                                                                                    style={{ opacity: 0.5, cursor: 'not-allowed' }}
                                                                                    onClick={() => pushAct({ id: `act_${Date.now()}`, type: "", product: "", product_id: "", quantity: "", product_value: "" })}
                                                                                >
                                                                                    + Add Activity
                                                                                </button> */}
                                                                            </div>
                                                                        );
                                                                    }}
                                                                </FieldArray>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Add Another Stop Button */}
                                                <div className="add_another_stop_row">
                                                    <button 
                                                        className="btn_add_another_stop" 
                                                        type="button" 
                                                        onClick={() => {
                                                            const loadProd = getLoadStopProductInfo(values);
                                                            push(getBlankStop(loadProd));
                                                            setFieldValue("end_location", "");
                                                        }}
                                                    >
                                                        + Add Another Stop
                                                    </button>
                                                </div>

                                                {/* Load vs unload balance is an array level rule, so its error lands on
                                                    errors.stops as a plain string instead of a per stop entry. */}
                                                {typeof errors.stops === 'string' && (
                                                    <div className="stops_balance_error">{errors.stops}</div>
                                                )}
                                            </>
                                        )}
                                    </FieldArray>

                                    {/* End Location Node */}
                                    <div className="timeline_step_item">
                                        <div className="timeline_node node_end">
                                            <span>✓</span>
                                        </div>
                                        <div className="timeline_content_box">
                                            {/* <div className="location_input_header_row">
                                                <button className="set_on_map_btn" type="button">Set on Map ▼</button>
                                            </div> */}
                                            <Select 
                                                label="End Location"
                                                name="end_location"
                                                placeholder="Select End Location"
                                                options={locationSelectOptions}
                                                value={locationSelectOptions.find(o => o.label === values.end_location || String(o.value) === String(values.end_location)) || (values.end_location ? { label: values.end_location, value: values.end_location } : null)}
                                                onChange={(opt) => handleEndLocationChange(opt, values, setFieldValue)}
                                                onBlur={handleBlur}
                                                error={errors.end_location}
                                                touched={touched.end_location}
                                                required={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* RIGHT COLUMN: Project LogsComments & Exact trip_notes_main_div for General Notes */}
                        <Col lg={4} md={12} className="planning_right_col">
                            {/* Driver Details Style Sidebar Box with LogsComments */}
                            <div className="driver_details_sidebar service_cost_summary_details mb-3">
                                <LogsComments />
                            </div>

                            {/* Exact Project standard trip_notes_main_div for General Notes */}
                            <div className="trip_notes_main_div">
                                <h5>General Notes</h5>
                                <div style={{ padding: '0 16px 16px 16px' }}>
                                    <Input 
                                        label=""
                                        name="general_notes"
                                        placeholder="Type your notes or additional info..."
                                        quill={true}
                                        value={values.general_notes}
                                        setFieldValue={setFieldValue}
                                        onBlur={handleBlur}
                                        error={errors.general_notes}
                                        touched={touched.general_notes}
                                        required={false}
                                    />
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
            )}
        </Formik>
    );
};

export default TripPlanningDetails;

